import { InlineMarkdown } from "@/components/markdown/InlineMarkdown.tsx";
import { cardSurfaceClass, clickableCardClass } from "@/components/threads/shared.tsx";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Card } from "@/components/ui/card.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";
import { EditMemoryDialog } from "./EditMemoryDialog.tsx";
import type { Memory } from "./types.ts";

/** Get color for quality score: red (low) -> yellow (mid) -> green (high) */
const getQualityColor = (score: number): string => {
  // Clamp score between 0 and 1
  const s = Math.max(0, Math.min(1, score));

  if (s < 0.5) {
    // Red to yellow (0-0.5)
    const ratio = s / 0.5;
    const r = 239; // red component stays high
    const g = Math.round(68 + (179 - 68) * ratio); // green increases
    const b = 68;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Yellow to green (0.5-1)
    const ratio = (s - 0.5) / 0.5;
    const r = Math.round(234 - (234 - 34) * ratio); // red decreases
    const g = Math.round(179 + (197 - 179) * ratio); // green increases slightly
    const b = Math.round(8 + (94 - 8) * ratio); // blue increases slightly
    return `rgb(${r}, ${g}, ${b})`;
  }
};

/** Short labels for temporal levels */
const temporalLevelLabels: Record<string, string> = {
  instant: "I",
  daily: "D",
  weekly: "W",
  monthly: "M",
};

/**
 * A memory row. The whole card is a single tap target that opens the plain-text
 * EditMemoryDialog (which also hosts Delete / Confirm / the admin graph link).
 * Content renders read-only via InlineMarkdown; pointer events are disabled on
 * it so a tap anywhere opens the editor rather than following an inline link.
 *
 * The renderer has to stay cheap: the list is unvirtualized and an established
 * account has hundreds of memories, so whatever this mounts, it mounts hundreds
 * of times in one commit.
 */
export const MemoryItem = ({ memory, isAdmin = false }: { memory: Memory; isAdmin?: boolean }) => {
  const [isEditing, setIsEditing] = useState(false);
  const isProcessing = memory.processing === true;

  const openEditor = () => {
    if (!isProcessing) setIsEditing(true);
  };

  return (
    <>
      <Card
        role="button"
        tabIndex={isProcessing ? -1 : 0}
        aria-label="Edit memory"
        onClick={openEditor}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEditor();
          }
        }}
        className={cn(
          // Layout only. Surface, radius, and padding come from the shared card
          // classes below, so a card's shape is defined once, in one place.
          "flex flex-row w-full items-center gap-2 transition-colors",
          // The thread-card hover reveal marks the card clickable; a processing
          // memory isn't, so it keeps the surface and drops the reveal.
          isProcessing ? cn(cardSurfaceClass, "opacity-80") : clickableCardClass,
        )}
      >
        {memory.subject.name && (
          <AvatarWithFallback
            size="md"
            person={{
              name: memory.subject.name,
              value: memory.subject.urn,
              picture: memory.subject.picture,
            }}
          />
        )}
        {/* `text-sm` to match every other card's primary line — without it the
            memory text inherits the 16px body size and reads a step larger than
            a contact's name or a routine's title sitting beside it. */}
        <div className="flex-1 min-w-0 pointer-events-none text-sm">
          <InlineMarkdown>{memory.content || ""}</InlineMarkdown>
        </div>
        {isProcessing && (
          <Tooltip>
            <TooltipTrigger>
              <Loader2 className="size-4 text-muted-foreground animate-spin shrink-0" />
            </TooltipTrigger>
            <TooltipContent>Processing memory...</TooltipContent>
          </Tooltip>
        )}
        {!isProcessing && memory.confirmed && (
          <CheckCircle2 className="size-4 text-green-500 shrink-0" aria-label="Confirmed memory" />
        )}
        {isAdmin && (
          <div className="flex items-center gap-2">
            {memory.temporalLevel && memory.temporalLevel !== "instant" && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-xs font-mono px-1.5 py-0">
                    {temporalLevelLabels[memory.temporalLevel] ?? memory.temporalLevel}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Temporal level: {memory.temporalLevel}</TooltipContent>
              </Tooltip>
            )}
            {memory.qualityScore !== undefined && (
              <Tooltip>
                <TooltipTrigger>
                  <div
                    className="size-3 rounded-full shrink-0"
                    style={{ backgroundColor: getQualityColor(memory.qualityScore) }}
                  />
                </TooltipTrigger>
                <TooltipContent>Quality: {Math.round(memory.qualityScore * 100)}%</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </Card>
      {isEditing && (
        <EditMemoryDialog
          memory={memory}
          open
          isAdmin={isAdmin}
          onOpenChange={(next) => {
            if (!next) setIsEditing(false);
          }}
        />
      )}
    </>
  );
};
